/**
 * /accounting/reports/income-statement — Live P&L with trade-level gross margin.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import {
  computeIncomeStatement, listAvailablePeriods, formatPeriodLabel, type PeriodKey,
} from "@/data/accounting/reports/financialReports";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;
const pct = (n: number) => `${n.toFixed(1)}%`;

export default function IncomeStatementPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries } = useGeneralLedger();
  const periods = useMemo<PeriodKey[]>(
    () => ["all", ...listAvailablePeriods(journalEntries)],
    [journalEntries],
  );
  const [period, setPeriod] = useState<PeriodKey>(periods[1] ?? "all");
  const pl = useMemo(
    () => computeIncomeStatement(accounts, journalEntries, period),
    [accounts, journalEntries, period],
  );

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to Reports
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 data-tour-id="income-statement-title" className="text-2xl font-semibold tracking-tight">Income Statement</h1>
            <p className="text-sm text-muted-foreground mt-1">{formatPeriodLabel(period)}</p>
          </div>
          <select
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-card"
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {periods.map(p => (
              <option key={p} value={p}>{formatPeriodLabel(p)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Header KPIs */}
      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Revenue" value={fmt(pl.totalRevenue)} />
          <Kpi label="COGS" value={fmt(pl.totalCogs)} />
          <Kpi label="Gross profit" value={fmt(pl.grossProfit)} hint={pct(pl.grossMarginPct)} />
          <Kpi label="Net income" value={fmt(pl.netIncome)} hint={pct(pl.netMarginPct)} tone={pl.netIncome < 0 ? "neg" : "pos"} />
        </div>
      </CockpitSection>

      {/* By trade */}
      {pl.byTrade.length > 0 && (
        <CockpitSection>
          <SectionHeader title="Gross profit by trade" />
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">Trade</th>
                  <th className="text-right px-3 py-2">Revenue</th>
                  <th className="text-right px-3 py-2">Material</th>
                  <th className="text-right px-3 py-2">Labor</th>
                  <th className="text-right px-3 py-2">Gross profit</th>
                  <th className="text-right px-3 py-2">GM %</th>
                </tr>
              </thead>
              <tbody>
                {pl.byTrade.map(t => (
                  <tr key={t.tradeType} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{t.tradeType}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.costOfMaterial)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.costOfLabor)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(t.grossProfit)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(t.grossMarginPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CockpitSection>
      )}

      {/* Detail */}
      <CockpitSection>
        <SectionHeader title="Detail" />
        <div className="rounded-md border border-border bg-card divide-y divide-border">
          <Block label="Revenue" rows={pl.revenueRows} total={pl.totalRevenue} />
          <Block label="Cost of Material" rows={pl.cogsMaterialRows} total={pl.cogsMaterialRows.reduce((s, r) => s + r.amount, 0)} />
          <Block label="Cost of Labor" rows={pl.cogsLaborRows} total={pl.cogsLaborRows.reduce((s, r) => s + r.amount, 0)} />
          <div className="px-3 py-2 flex justify-between text-sm font-semibold bg-muted/30">
            <span>Gross profit</span>
            <span className="tabular-nums">{fmt(pl.grossProfit)} <span className="text-muted-foreground font-normal">· {pct(pl.grossMarginPct)}</span></span>
          </div>
          <Block label="Operating Expenses" rows={pl.opexRows} total={pl.totalOpex} />
          <div className="px-3 py-2 flex justify-between text-sm font-semibold bg-muted/30">
            <span>Net income</span>
            <span className={`tabular-nums ${pl.netIncome < 0 ? "text-destructive" : ""}`}>
              {fmt(pl.netIncome)} <span className="text-muted-foreground font-normal">· {pct(pl.netMarginPct)}</span>
            </span>
          </div>
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "pos" | "neg" }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone === "neg" ? "text-destructive" : ""}`}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{title}</div>;
}

function Block({ label, rows, total }: {
  label: string;
  rows: { accountId: string; number: string; name: string; amount: number }[];
  total: number;
}) {
  if (rows.length === 0) {
    return (
      <div className="px-3 py-2 flex justify-between text-sm text-muted-foreground">
        <span>{label}</span><span className="tabular-nums">{`$0`}</span>
      </div>
    );
  }
  return (
    <div className="py-2">
      <div className="px-3 text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
      {rows.map(r => (
        <Link
          key={r.accountId}
          to={`/accounting/gl/detail?account=${r.accountId}`}
          className="px-3 py-1 flex justify-between text-sm hover:bg-accent/40 rounded-sm"
          title="View GL detail for this account"
        >
          <span className="text-muted-foreground"><span className="tabular-nums mr-2">{r.number}</span>{r.name}</span>
          <span className="tabular-nums">{`${r.amount < 0 ? "-" : ""}$${Math.abs(Math.round(r.amount)).toLocaleString()}`}</span>
        </Link>
      ))}
      <div className="px-3 pt-1 flex justify-between text-sm font-medium border-t border-border mt-1">
        <span>Total {label.toLowerCase()}</span>
        <span className="tabular-nums">{`${total < 0 ? "-" : ""}$${Math.abs(Math.round(total)).toLocaleString()}`}</span>
      </div>
    </div>
  );
}
